import type { LucideIcon } from "lucide-react";
import {
  Activity,
  ArrowLeftRight,
  BadgeDollarSign,
  Bell,
  BookOpen,
  BookText,
  Boxes,
  Building2,
  CalendarClock,
  ClipboardList,
  CreditCard,
  Database,
  FileText,
  Files,
  Gauge,
  GitBranch,
  Headset,
  Home,
  KeyRound,
  Layers,
  LayoutDashboard,
  LifeBuoy,
  ListChecks,
  Mail,
  Map,
  MessageSquare,
  Package,
  Plug,
  Printer,
  Route,
  ScrollText,
  Server,
  Settings,
  Shield,
  Sparkles,
  Tag,
  Truck,
  Undo2,
  Upload,
  UserCog,
  Users,
  Warehouse,
  Webhook,
  Wrench,
} from "lucide-react";
import type { Role } from "@ftpedi/contracts";
import type { SessionState } from "@/lib/session.tsx";

export interface NavItem {
  to: string;
  label: string;
  icon: LucideIcon;
  feature?: string;
  roles?: Role[];
  badgeKey?: string;
  exact?: boolean;
}

export interface NavGroup {
  label: string;
  items: NavItem[];
}

const CUSTOMER_ADMIN: Role[] = ["customer_admin", "operator_admin", "operator_staff", "platform_admin"];

const adminNav: NavGroup[] = [
  {
    label: "Overview",
    items: [
      { to: "/", label: "Dashboard", icon: LayoutDashboard, exact: true },
      { to: "/documents", label: "Documents", icon: FileText },
      { to: "/staff", label: "Staff desk", icon: Headset },
      { to: "/shipments", label: "Shipments", icon: Truck },
    ],
  },
  {
    label: "Customers",
    items: [
      { to: "/companies", label: "Companies", icon: Building2 },
      { to: "/users", label: "Portal & staff logins", icon: Users },
      { to: "/sftp-accounts", label: "SFTP accounts", icon: Server },
      { to: "/wms", label: "WMS", icon: Warehouse },
      { to: "/transport", label: "Transport (TMS)", icon: Truck },
    ],
  },
  {
    label: "Trading partners",
    items: [
      { to: "/partners", label: "Partners", icon: ArrowLeftRight },
      { to: "/catalogue", label: "Catalogue", icon: BookOpen },
      { to: "/maps", label: "Maps", icon: Map },
      { to: "/routes", label: "Routes", icon: Route },
      { to: "/document-types", label: "Document types", icon: Files },
      { to: "/as2", label: "AS2", icon: Shield, feature: "as2" },
    ],
  },
  {
    label: "Logistics",
    items: [
      { to: "/master-data", label: "Master data", icon: Database },
      { to: "/labels", label: "Labels", icon: Tag, feature: "labels" },
      { to: "/paperwork", label: "Paperwork", icon: Printer, feature: "labels" },
      { to: "/tools", label: "Tools", icon: Wrench, feature: "tools" },
    ],
  },
  {
    label: "Operations",
    items: [
      { to: "/alerts", label: "Alerts", icon: Activity, feature: "alerts", badgeKey: "alerts" },
      { to: "/notifications", label: "Notifications", icon: Bell, badgeKey: "notifications" },
      { to: "/reports", label: "Reports", icon: ClipboardList, feature: "reports" },
      { to: "/scheduler", label: "Scheduler", icon: CalendarClock },
      { to: "/audit", label: "Audit", icon: ScrollText },
      { to: "/feedback", label: "Feedback", icon: MessageSquare },
    ],
  },
  {
    label: "Integrations",
    items: [
      { to: "/api-clients", label: "API clients", icon: KeyRound, feature: "api" },
      { to: "/webhooks", label: "Webhooks", icon: Webhook, feature: "api" },
    ],
  },
  {
    label: "Account & billing",
    items: [
      { to: "/settings", label: "Settings", icon: Settings },
      { to: "/billing", label: "Billing", icon: CreditCard },
      { to: "/onboarding", label: "Onboarding", icon: ListChecks },
      { to: "/ai", label: "AI helper", icon: Sparkles, feature: "ai" },
    ],
  },
  {
    label: "Platform",
    items: [
      { to: "/platform/operators", label: "Operators", icon: Layers, roles: ["platform_admin"] },
      { to: "/platform/plans", label: "Plans", icon: BadgeDollarSign, roles: ["platform_admin"] },
      { to: "/platform/tools", label: "Tools", icon: Wrench, roles: ["platform_admin"] },
      { to: "/platform/catalogue", label: "Catalogue review", icon: BookText, roles: ["platform_admin"] },
      { to: "/platform/queues", label: "Queues", icon: Gauge, roles: ["platform_admin"] },
      { to: "/platform/usage", label: "Usage", icon: Boxes, roles: ["platform_admin"] },
      { to: "/platform/audit", label: "Audit", icon: ScrollText, roles: ["platform_admin"] },
      { to: "/platform/runbooks", label: "Runbooks", icon: GitBranch, roles: ["platform_admin"] },
      { to: "/platform/enquiries", label: "Enquiries", icon: Mail, roles: ["platform_admin"] },
    ],
  },
];

const portalNav: NavGroup[] = [
  {
    label: "Workspace",
    items: [
      { to: "/", label: "Home", icon: Home, exact: true },
      { to: "/upload", label: "Upload", icon: Upload },
      { to: "/documents", label: "Documents", icon: FileText },
      { to: "/shipments", label: "Shipments", icon: Truck },
      { to: "/returns", label: "Returns", icon: Undo2 },
    ],
  },
  {
    label: "Setup",
    items: [
      { to: "/partners", label: "Trading partners", icon: ArrowLeftRight, feature: "customer_partner_setup" },
      { to: "/products", label: "Products", icon: Package },
      { to: "/people", label: "People", icon: UserCog, roles: CUSTOMER_ADMIN },
      { to: "/connections", label: "Connections", icon: Plug, roles: CUSTOMER_ADMIN },
      { to: "/api-access", label: "API access", icon: KeyRound, roles: CUSTOMER_ADMIN, feature: "api" },
    ],
  },
  {
    label: "Insights",
    items: [
      { to: "/reports", label: "Reports", icon: ClipboardList },
      { to: "/notifications", label: "Notifications", icon: Bell, badgeKey: "notifications" },
    ],
  },
  {
    label: "Support",
    items: [
      { to: "/help", label: "Help", icon: LifeBuoy },
      { to: "/feedback", label: "Feedback", icon: MessageSquare },
    ],
  },
];

export function navFor(session: Pick<SessionState, "surface" | "me" | "hasFeature" | "isPlatform">): NavGroup[] {
  const source = session.surface === "portal" ? portalNav : adminNav;
  const role = session.me?.role;
  return source
    .map((g) => ({
      label: g.label,
      items: g.items.filter((i) => (!i.feature || session.hasFeature(i.feature)) && (!i.roles || (role !== undefined && i.roles.includes(role)))),
    }))
    .filter((g) => g.items.length > 0 && (g.label !== "Platform" || session.isPlatform));
}

export function flattenNav(groups: NavGroup[]): NavItem[] {
  return groups.flatMap((g) => g.items);
}

export function navLabelFor(groups: NavGroup[], pathname: string): string | undefined {
  return flattenNav(groups).find((i) => i.to === pathname)?.label;
}
